if (!customElements.get('cart-upsell')) {
  customElements.define(
    'cart-upsell',
    class CartUpsell extends HTMLElement {
      connectedCallback() {
        // In the drawer, sit under the cart items (inside the scroll area) instead of in the fixed footer,
        // so the total and Checkout stay visible. Moving re-runs connectedCallback.
        if (this.dataset.context === 'drawer') {
          const items = this.closest('cart-drawer')?.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper');
          if (items && this.previousElementSibling !== items) {
            items.after(this);
            return;
          }
        }
        if (this.loaded) return;
        this.loaded = true;
        this.list = this.querySelector('.cart-upsell__list');
        this.errorEl = this.querySelector('.cart-upsell__error');
        this.load().catch(() => this.hide());
      }

      hide() {
        this.hidden = true;
      }

      fetchIntent(productId, intent) {
        // Session cache shared by all instances (drawer re-renders create new elements).
        const cache = (window.__cartUpsellCache = window.__cartUpsellCache || new Map());
        const key = `${productId}:${intent}`;
        if (!cache.has(key)) {
          const base = this.dataset.recommendationsUrl || '/recommendations/products';
          const request = fetch(`${base}.json?product_id=${productId}&intent=${intent}&limit=6`)
            .then((res) => (res.ok ? res.json() : { products: [] }))
            .then((data) => data.products || [])
            .catch(() => {
              cache.delete(key);
              return [];
            });
          cache.set(key, request);
        }
        return cache.get(key);
      }

      async load() {
        const ids = (this.dataset.productIds || '').split(',').filter(Boolean).map(Number);
        if (!ids.length || !window.CartUpsellLogic) return this.hide();
        const seedResults = await Promise.all(
          ids.slice(0, 3).map(async (id) => {
            const [complementary, related] = await Promise.all([
              this.fetchIntent(id, 'complementary'),
              this.fetchIntent(id, 'related'),
            ]);
            return { complementary, related };
          })
        );
        const products = window.CartUpsellLogic.pickSuggestions({
          seedResults,
          cartProductIds: ids,
          cartHasAdult: this.dataset.hasAdult === 'true',
          limit: 3,
        });
        const rows = products.map((product) => this.row(product)).filter(Boolean);
        if (!rows.length) return this.hide();
        this.list.replaceChildren(...rows);
        this.removeAttribute('aria-busy');
      }

      row(product) {
        const variant = (product.variants || []).find((v) => v.available);
        if (!variant) return null;
        const fmt = (cents) => window.CartUpsellLogic.formatMoney(cents, this.dataset.moneyFormat);
        const li = document.createElement('li');
        li.className = 'cart-upsell__item';

        const thumb = document.createElement('a');
        thumb.className = 'cart-upsell__thumb';
        thumb.href = product.url;
        thumb.tabIndex = -1;
        thumb.setAttribute('aria-hidden', 'true');
        if (product.featured_image) {
          const img = document.createElement('img');
          const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
          img.src = `${src}${src.includes('?') ? '&' : '?'}width=120`;
          img.alt = '';
          img.width = 56;
          img.height = 56;
          img.decoding = 'async';
          thumb.appendChild(img);
        }

        const text = document.createElement('span');
        text.className = 'cart-upsell__text';
        const name = document.createElement('a');
        name.className = 'cart-upsell__name';
        name.href = product.url;
        name.textContent = product.title;
        const price = document.createElement('span');
        price.className = 'cart-upsell__price';
        if (variant.compare_at_price > variant.price) {
          const was = document.createElement('s');
          was.textContent = fmt(variant.compare_at_price);
          price.appendChild(was);
        }
        price.appendChild(document.createTextNode(fmt(variant.price)));
        text.append(name, price);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-upsell__add';
        button.dataset.variantId = variant.id;
        button.textContent = '+ Add';
        button.setAttribute('aria-label', `Add ${product.title} to cart`);
        button.addEventListener('click', () => this.add(button));

        li.append(thumb, text, button);
        return li;
      }

      sections() {
        if (this.dataset.context === 'drawer') return ['cart-drawer', 'cart-icon-bubble'];
        return [
          document.getElementById('main-cart-items')?.dataset.id,
          document.getElementById('main-cart-footer')?.dataset.id,
          'cart-icon-bubble',
        ].filter(Boolean);
      }

      async add(button) {
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding…';
        this.errorEl.hidden = true;
        try {
          const res = await fetch(window.routes.cart_add_url, {
            ...fetchConfig('javascript'),
            body: JSON.stringify({
              items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
              sections: this.sections(),
              sections_url: window.location.pathname,
            }),
          });
          const state = await res.json();
          if (!res.ok || state.status) throw new Error(state.description || state.message);
          this.refresh(state);
        } catch (err) {
          this.errorEl.textContent = (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
          this.errorEl.hidden = false;
          button.removeAttribute('aria-busy');
          button.textContent = '+ Add';
        }
      }

      refresh(state) {
        const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

        if (this.dataset.context === 'drawer') {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            drawer.renderContents({ id: state.items?.[0]?.id, sections: state.sections });
            return;
          }
        }

        // Cart page: swap the whole inner content of items + footer (the new footer
        // contains a fresh <cart-upsell>, which reloads its suggestions on connect).
        [
          ['main-cart-items', document.getElementById('main-cart-items')?.dataset.id],
          ['main-cart-footer', document.getElementById('main-cart-footer')?.dataset.id],
        ].forEach(([elementId, sectionId]) => {
          const html = sectionId && state.sections?.[sectionId];
          const target = document.getElementById(elementId);
          const source = html && parse(html).getElementById(elementId);
          if (target && source) {
            target.innerHTML = source.innerHTML;
            target.classList.remove('is-empty');
          }
        });

        const bubbleHtml = state.sections?.['cart-icon-bubble'];
        const bubble = document.getElementById('cart-icon-bubble');
        const bubbleSource = bubbleHtml && parse(bubbleHtml).querySelector('.shopify-section');
        if (bubble && bubbleSource) bubble.innerHTML = bubbleSource.innerHTML;
      }
    }
  );
}
